import React, { useState } from 'react';
import { Stack, Typography, Button, Alert, Box, Chip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { History as HistoryIcon, Delete as DeleteIcon } from '@mui/icons-material';

interface FormHeaderProps {
    isMobile?: boolean;
    showSuccess?: boolean;
    onViewHistory?: () => void;
    inspectionId?: number | null;
    isAdmin?: boolean;
    onDelete?: () => void;
}

const FormHeader: React.FC<FormHeaderProps> = ({
    isMobile = false,
    showSuccess = false,
    onViewHistory,
    inspectionId,
    onDelete,
}) => {
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const handleDeleteClick = () => {
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        setDeleteDialogOpen(false);
        if (onDelete) {
            onDelete();
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
    };

    return (
        <>
            <Stack
                direction={isMobile ? 'column' : 'row'}
                justifyContent="space-between"
                alignItems={isMobile ? 'stretch' : 'center'}
                spacing={isMobile ? 2 : 0}
                sx={{ mb: isMobile ? 3 : 3 }}
            >
                <Box>
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                        Storage Inspection Form
                    </Typography>
                    {inspectionId && isMobile && (
                        <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', mt: 0.5 }}
                        >
                            ID: {inspectionId}
                        </Typography>
                    )}
                </Box>
                <Stack
                    direction={isMobile ? 'column' : 'row'}
                    spacing={isMobile ? 1 : 2}
                    alignItems="center"
                    sx={{ width: isMobile ? '100%' : 'auto' }}
                >
                    {inspectionId && !isMobile && (
                        <Chip
                            label={`ID: ${inspectionId}`}
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                        />
                    )}
                    {onViewHistory && (
                        <Button
                            variant="outlined"
                            startIcon={!isMobile && <HistoryIcon />}
                            onClick={onViewHistory}
                            size={isMobile ? 'small' : 'medium'}
                            fullWidth={isMobile}
                        >
                            {isMobile ? 'History' : 'View History'}
                        </Button>
                    )}
                </Stack>
            </Stack>

            {showSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    Form saved successfully!
                </Alert>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Delete Inspection Form?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete this inspection form (ID: {inspectionId})?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FormHeader;
